import {
  Building2,
  Shield,
  Phone,
  Lock,
  BarChart3,
  Headphones,
  type LucideIcon,
} from 'lucide-react'

export interface IndustryChallenge {
  title: string
  description: string
}

export interface IndustrySolution {
  title: string
  description: string
  icon: LucideIcon
}

export interface Industry {
  id: string
  title: string
  shortTitle: string
  description: string
  longDescription: string
  icon: LucideIcon
  href: string
  challenges: IndustryChallenge[]
  solutions: IndustrySolution[]
  stats: Array<{ value: string; label: string }>
}

export const industries: Industry[] = [
  {
    id: 'credit-unions',
    title: 'Technology Solutions for Credit Unions',
    shortTitle: 'Credit Unions',
    description: 'Purpose-built technology solutions that help credit unions deliver exceptional member experiences while maintaining security and compliance.',
    longDescription: 'UC Advisors understands the unique challenges credit unions face. Our team has decades of combined experience working with credit unions of all sizes, from small community institutions to large regional organizations. We deliver technology solutions that improve member service, reduce operational costs, and ensure regulatory compliance.',
    icon: Building2,
    href: '/industries/credit-unions',
    challenges: [
      {
        title: 'Member Experience Expectations',
        description: 'Members expect seamless, omnichannel service comparable to big banks.',
      },
      {
        title: 'Regulatory Compliance',
        description: 'NCUA, FFIEC, and other regulations require robust security and documentation.',
      },
      {
        title: 'Legacy System Integration',
        description: 'Core banking systems need to work with modern communication tools.',
      },
      {
        title: 'Limited IT Resources',
        description: 'Small IT teams must manage complex technology infrastructure.',
      },
    ],
    solutions: [
      {
        title: 'Member Contact Center',
        description: 'Omnichannel contact center solutions that integrate with your core banking system for personalized member service.',
        icon: Headphones,
      },
      {
        title: 'Unified Communications',
        description: 'Connect your branches and remote staff with secure voice, video, and messaging.',
        icon: Phone,
      },
      {
        title: 'Security & Compliance',
        description: 'Cybersecurity solutions designed for financial institution requirements.',
        icon: Shield,
      },
      {
        title: 'Network Infrastructure',
        description: 'Reliable, secure networking that supports all your digital services.',
        icon: Building2,
      },
    ],
    stats: [
      { value: '50+', label: 'Credit Unions Served' },
      { value: '99.99%', label: 'System Uptime' },
      { value: '40%', label: 'Average Cost Savings' },
      { value: '24/7', label: 'Support Available' },
    ],
  },
  {
    id: 'banking',
    title: 'Technology Solutions for Banks & Financial Services',
    shortTitle: 'Banks & Financial Services',
    description: 'Enterprise-grade technology solutions that help banks modernize operations, enhance customer engagement, and maintain regulatory compliance.',
    longDescription: 'UC Advisors partners with banks and financial institutions to deliver technology solutions that drive digital transformation. From community banks to large regional institutions, we provide the expertise and solutions needed to compete in today\'s digital-first financial landscape.',
    icon: Building2,
    href: '/industries/banking',
    challenges: [
      {
        title: 'Digital Transformation',
        description: 'Modernizing legacy systems while maintaining operational continuity.',
      },
      {
        title: 'Customer Experience',
        description: 'Meeting customer expectations for digital-first, personalized service.',
      },
      {
        title: 'Cybersecurity Threats',
        description: 'Protecting against increasingly sophisticated cyber attacks.',
      },
      {
        title: 'Regulatory Requirements',
        description: 'Navigating complex compliance requirements across multiple agencies.',
      },
    ],
    solutions: [
      {
        title: 'Enterprise Contact Center',
        description: 'AI-powered contact center solutions that deliver personalized customer experiences at scale.',
        icon: Headphones,
      },
      {
        title: 'Secure Communications',
        description: 'Encrypted voice, video, and messaging for internal and customer communications.',
        icon: Lock,
      },
      {
        title: 'Branch Connectivity',
        description: 'SD-WAN and networking solutions that connect branches securely and cost-effectively.',
        icon: Building2,
      },
      {
        title: 'Performance Analytics',
        description: 'Real-time dashboards and reporting for operational visibility.',
        icon: BarChart3,
      },
    ],
    stats: [
      { value: '30+', label: 'Banks Served' },
      { value: '$2M+', label: 'Annual Savings Delivered' },
      { value: '100%', label: 'Compliance Success' },
      { value: '4.9/5', label: 'Customer Satisfaction' },
    ],
  },
]

export const getIndustryById = (id: string) => industries.find(i => i.id === id)

